import { createSignal, createResource, createEffect, For, Show } from 'solid-js';
import { formatMoney as fmt } from '../lib/money.js';
import { runStatusLabel } from '../lib/labels.js';

// Laporan — accounting views over one run: per-component recap and the
// bank transfer list. Visibility-INDEPENDENT (a hidden slip line is still
// real money); grand totals come from the server's strict Money type.

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

const KIND_LABEL = { earning: 'Pendapatan', deduction: 'Potongan', info: 'Info' };

export default function Laporan() {
  const [schemes] = createResource(() => getJSON('/api/schemes'));
  const [scheme, setScheme] = createSignal('');
  createEffect(() => {
    const list = schemes();
    if (list?.length && !scheme()) setScheme(list[0].code);
  });

  const [runs] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/runs?scheme=${code}`) : null,
  );
  const [runId, setRunId] = createSignal(0);
  createEffect(() => {
    const list = runs();
    if (list?.length) setRunId(list[0].id);
    else setRunId(0);
  });

  const [report] = createResource(
    runId,
    (id) => id ? getJSON(`/api/run/${id}/report`) : null,
  );

  const [view, setView] = createSignal('rekap'); // rekap | transfer

  // Excel id-ID expects ';' separators; ﻿ BOM makes it open as UTF-8.
  function exportCSV() {
    const r = report();
    if (!r) return;
    const rows = [
      ['NIK', 'Nama', 'Bank', 'Rekening', 'Atas Nama', 'Jumlah'],
      ...r.transfers.map(t => [t.nik, t.name, t.bankName ?? '', t.bankAccount ?? '', t.bankHolder ?? '', t.amount]),
      ['', '', '', '', 'TOTAL', r.bersih.amount],
    ];
    const csv = '﻿' + rows.map(row =>
      row.map(c => /[";\n]/.test(c) ? '"' + c.replaceAll('"', '""') + '"' : c).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `transfer-${r.run.scheme}-${r.run.periodStart}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <>
      <div class="page-head lp-controls">
        <h2>Laporan</h2>
        <div class="spacer" />
        <Show when={schemes()}>
          <div class="seg">
            <For each={schemes()}>{(s) =>
              <button class="seg-item" classList={{ active: scheme() === s.code }}
                onClick={() => setScheme(s.code)}>{s.code}</button>
            }</For>
          </div>
        </Show>
        <select class="kg-select" value={runId()} onInput={(e) => setRunId(Number(e.currentTarget.value))}>
          <For each={runs() ?? []}>{(r) =>
            <option value={r.id}>{r.periodStart} — {runStatusLabel(r.status)}{r.legacyNo ? ` (${r.legacyNo})` : ''}</option>
          }</For>
        </select>
      </div>

      <Show when={report()}>
        <div class="stat-grid lp-stats">
          <div class="card stat">
            <div class="label">Total Pendapatan</div>
            <div class="value money earning">{fmt(report().bruto.amount)}</div>
          </div>
          <div class="card stat">
            <div class="label">Total Potongan</div>
            <div class="value money deduction">{fmt(report().potongan.amount)}</div>
          </div>
          <div class="card stat">
            <div class="label">Total Bersih</div>
            <div class="value money">{fmt(report().bersih.amount)}</div>
          </div>
          <div class="card stat">
            <div class="label">Karyawan</div>
            <div class="value">{report().transfers.length}</div>
            <Show when={report().missingBank > 0}>
              <div class="hint lp-warn">{report().missingBank} tanpa data rekening</div>
            </Show>
          </div>
        </div>

        <div class="lp-bar lp-controls">
          <div class="seg">
            <button class="seg-item" classList={{ active: view() === 'rekap' }} onClick={() => setView('rekap')}>Rekap Komponen</button>
            <button class="seg-item" classList={{ active: view() === 'transfer' }} onClick={() => setView('transfer')}>Transfer Bank</button>
          </div>
          <div class="spacer" />
          <Show when={view() === 'transfer'}>
            <button class="btn" onClick={exportCSV}>Export CSV</button>
          </Show>
          <button class="btn primary" onClick={() => window.print()}>Cetak</button>
        </div>

        <div class="card lp-print" id="laporan-print">
          <div class="lp-print-head">
            <b>Laporan {view() === 'rekap' ? 'Rekap Komponen' : 'Transfer Bank'}</b> —
            {' '}{report().run.scheme}, periode {report().run.periodStart} s/d {report().run.periodEnd}
            <Show when={report().run.legacyNo}> · No. {report().run.legacyNo}</Show>
          </div>

          <Show when={view() === 'rekap'}>
            <table class="data">
              <thead>
                <tr><th>Komponen</th><th>Jenis</th><th class="num">Karyawan</th><th class="num">Total</th></tr>
              </thead>
              <tbody>
                <For each={report().components}>{(c) =>
                  <tr>
                    <td><span class={`kdot ${c.kind === 'earning' ? 'earning' : c.kind === 'deduction' ? 'deduction' : 'calc'}`} /> {c.label}</td>
                    <td>{KIND_LABEL[c.kind] ?? c.kind}</td>
                    <td class="num">{c.employees}</td>
                    <td class={`num money ${c.kind === 'earning' ? 'earning' : c.kind === 'deduction' ? 'deduction' : ''}`}>{fmt(c.total)}</td>
                  </tr>
                }</For>
              </tbody>
            </table>
          </Show>

          <Show when={view() === 'transfer'}>
            <table class="data">
              <thead>
                <tr><th>NIK</th><th>Nama</th><th>Bank</th><th>Rekening</th><th>Atas Nama</th><th class="num">Jumlah</th></tr>
              </thead>
              <tbody>
                <For each={report().transfers}>{(t) =>
                  <tr classList={{ 'lp-nobank': !t.bankAccount }}>
                    <td class="ky-nik">{t.nik}</td>
                    <td>{t.name}</td>
                    <td>{t.bankName || '—'}</td>
                    <td class="ky-nik">{t.bankAccount || '—'}</td>
                    <td>{t.bankHolder || ''}</td>
                    <td class="num money">{fmt(t.amount)}</td>
                  </tr>
                }</For>
                <tr class="lp-total">
                  <td colspan="5"><b>TOTAL ({report().bersih.currency})</b></td>
                  <td class="num money"><b>{fmt(report().bersih.amount)}</b></td>
                </tr>
              </tbody>
            </table>
          </Show>
        </div>
      </Show>
    </>
  );
}
